import { useQuery } from '@tanstack/react-query';
import { Loader2, Radio } from 'lucide-react';
import { useIslands } from '@/lib/islands';
import { fetchDiagnostic } from '@/lib/logs-api';
import { zgrupujLive } from '@/lib/live-smart-sync';
import { useI18n } from '@/lib/i18n';

/**
 * KARTA „LIVE" — co wleciało ze Smart Synca, na żywo.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZGŁOSZONE (01.09.2026, IKEA): „potrzebujemy w logach dodać kartę »live«, która pokaże
 *  wszystko, co wleciało ze Smart Sync (ale raczej tylko to, co się zmieniło)".
 *
 *  ⚠️⚠️ TA KARTA JEST PRAWIE PUSTA NA ZDROWEJ WYSPIE I TO JEST JEJ TREŚĆ. Portal zapisuje zdarzenie
 *  WYŁĄCZNIE wtedy, gdy paczka coś zmieniła (`zdarzenie-live.ts`) — dwanaście węzłów pukających co
 *  kilka sekund dałoby kilkaset tysięcy wierszy na dobę, w których nie da się nic znaleźć. Ruch na
 *  tej karcie znaczy więc, że coś się NAPRAWDĘ stało.
 *
 *  ⚠️ ODŚWIEŻAMY CO 3 SEKUNDY, ale bez migania: `placeholderData` trzyma poprzednią listę do czasu
 *  odpowiedzi, więc wiersze nie skaczą, a nowe dochodzą na górę.
 *
 *  ⚠️ CZYTAMY DZIENNIK DIAGNOSTYCZNY PO ŹRÓDLE, a nie osobną tabelę: dziennik ma już retencję,
 *  zakres wyspy i uprawnienie. Druga tabela na to samo znaczyłaby drugą retencję do napisania.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
const ODSWIEZANIE_MS = 3_000;

export function LiveSmartSync() {
  const { t } = useI18n();
  const { groupId } = useIslands();

  const { data, isPending, isFetching } = useQuery({
    queryKey: ['logs', 'live', groupId],
    queryFn: () =>
      fetchDiagnostic({
        ...(groupId === null ? {} : { groupId }),
        source: 'smart-sync-live',
        page: 1,
        pageSize: 100,
      }),
    refetchInterval: ODSWIEZANIE_MS,
    placeholderData: (poprzednie) => poprzednie,
  });

  /**
   * ⚠️⚠️ JEDEN CYKL WĘZŁA TO JEDEN WIERSZ — zgłoszone 11.09.2026: „te logi live wydają się
   * zdublowane i są dość nieczytelne". Portal pisze osobny wpis na każdą tabelę konfiguracji
   * i osobny na wydania, a agent dosyła wydania przeplotem co 10 s przy cyklu co 60 s — jeden
   * cykl jednej maszyny dawał więc trzy–cztery wiersze z tą samą nazwą węzła i tą samą jedynką.
   * Zbijanie i sumowanie stoi w `lib/live-smart-sync.ts` (czyste, ze spekiem).
   */
  const grupy = zgrupujLive(data?.rows ?? [], {
    wydania: (n) => t(n === 1 ? 'logs.live.wydanie' : 'logs.live.wydan').replace('{n}', String(n)),
    zachowane: (n) => t('logs.live.zachowane').replace('{n}', String(n)),
  });

  return (
    <div className="space-y-2">
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Radio className={isFetching ? 'size-3.5 animate-pulse text-[var(--success)]' : 'size-3.5'} />
        {t('logs.live.opis')}
      </p>

      {isPending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {t('common.loading')}
        </p>
      ) : grupy.length === 0 ? (
        /** ⚠️ PUSTO TO DOBRA WIADOMOŚĆ — i tak trzeba to napisać, żeby nikt nie szukał usterki. */
        <p className="rounded border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          {t('logs.live.pusto')}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="p-2">{t('logs.live.kiedy')}</th>
                <th className="p-2">{t('logs.live.wezel')}</th>
                <th className="p-2">{t('logs.live.co')}</th>
              </tr>
            </thead>
            <tbody>
              {grupy.map((g) => (
                <tr key={g.seq} className="border-t">
                  <td className="p-2 whitespace-nowrap tabular-nums text-muted-foreground">
                    {new Date(g.at).toLocaleTimeString('pl')}
                  </td>
                  <td className="p-2 font-mono text-[11px]">{g.wezel}</td>
                  {/*
                    ⚠️ CZŁONY ODDZIELONE KROPKĄ, nie sklejone zdania: „2 wydania · Items +3" czyta
                    się jednym rzutem oka, a każdy człon jest SUMĄ, a nie powtórzonym wpisem.
                  */}
                  <td className="p-2">{g.czesci.join(' · ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
